/**
 * use-inbox-views.ts — "Chế độ xem tuỳ chỉnh" của Hộp thư (cá nhân / chung).
 * Backend: ecdp_backend_source/src/modules/chat/inbox-saved-views-routes.ts
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import type { ConditionLogic, InboxCondition } from '@/pages/conversations/inbox-conditions'

export type ViewScope = 'personal' | 'shared'

export interface InboxSavedView {
  id: string
  name: string
  scope: ViewScope
  conditions: InboxCondition[]
  logic: ConditionLogic
  position: number
  ownerUserId: string
  ownerName: string | null
  /** Người đang xem được đổi tên / sửa điều kiện / xoá không. */
  canEdit: boolean
}

export interface InboxViewsResponse {
  views: InboxSavedView[]
  /** Được tạo chế độ xem chung (Chủ sở hữu / Quản trị viên / Quản lý). */
  canManageShared: boolean
}

export interface SaveViewInput {
  name: string
  scope: ViewScope
  conditions: InboxCondition[]
  logic: ConditionLogic
}

const KEY = ['inbox-views'] as const

export function useInboxViews() {
  return useQuery<InboxViewsResponse>({
    queryKey: KEY,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await api.get<InboxViewsResponse>('/inbox-views')
      return data
    },
  })
}

/** Số hội thoại khớp từng chế độ xem — cùng tập nền với số đếm của cột bộ lọc. */
export function useInboxViewCounts(params: { tab?: string; accountId?: string; channel?: string; archived?: boolean }, enabled = true) {
  const query: Record<string, string> = {}
  if (params.tab) query.tab = params.tab
  if (params.accountId) query.accountId = params.accountId
  if (params.channel) query.channel = params.channel
  if (params.archived) query.archived = '1'
  return useQuery<Record<string, number | null>>({
    queryKey: ['inbox-view-counts', query],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      const { data } = await api.get<{ counts: Record<string, number | null> }>('/inbox-views/counts', { params: query })
      return data.counts
    },
  })
}

function useInvalidate() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: KEY })
    qc.invalidateQueries({ queryKey: ['inbox-view-counts'] })
  }
}

export function useCreateInboxView() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (input: SaveViewInput) => {
      const { data } = await api.post<InboxSavedView>('/inbox-views', input)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useUpdateInboxView() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<SaveViewInput> & { id: string }) => {
      const { data } = await api.patch<InboxSavedView>(`/inbox-views/${id}`, patch)
      return data
    },
    onSuccess: invalidate,
  })
}

export function useDeleteInboxView() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/inbox-views/${id}`)
      return id
    },
    onSuccess: invalidate,
  })
}
